import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Ticket, User, Book, X, Loader2, Command } from 'lucide-react';
import api from '../utils/api';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ tickets: [], users: [], articles: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults({ tickets: [], users: [], articles: [] });
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults({ tickets: [], users: [], articles: [] });
      return;
    }
    setLoading(true);
    try {
      const [ticketsRes, usersRes, articlesRes] = await Promise.allSettled([
        api.get('/tickets', { params: { search: q, limit: 5 } }),
        api.get('/users/search', { params: { q, limit: 5 } }),
        api.get('/kb/search', { params: { q, limit: 5 } })
      ]);
      setResults({
        tickets: ticketsRes.status === 'fulfilled' ? (ticketsRes.value.data.tickets || ticketsRes.value.data) : [],
        users: usersRes.status === 'fulfilled' ? (usersRes.value.data || []) : [],
        articles: articlesRes.status === 'fulfilled' ? (articlesRes.value.data || []) : []
      });
    } catch (err) {
      console.error('Erreur recherche:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const allResults = [
    ...results.tickets.map(t => ({ type: 'ticket', data: t, label: `${t.ticket_number} - ${t.subject}`, path: `/tickets/${t.ticket_number}` })),
    ...results.users.map(u => ({ type: 'user', data: u, label: `${u.name} (${u.email})`, path: `/users` })),
    ...results.articles.map(a => ({ type: 'article', data: a, label: a.title, path: `/kb/${a.id}` }))
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      navigate(allResults[selectedIndex].path);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'ticket': return Ticket;
      case 'user': return User;
      case 'article': return Book;
      default: return Search;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'ticket': return 'Ticket';
      case 'user': return 'Utilisateur';
      case 'article': return 'Article';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher tickets, utilisateurs, articles..."
            className="flex-1 bg-transparent border-0 outline-none text-gray-900 dark:text-white placeholder-gray-400 text-lg"
          />
          {loading && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
            ESC
          </kbd>
        </div>

        {allResults.length > 0 && (
          <div className="max-h-80 overflow-y-auto py-2">
            {allResults.map((item, idx) => {
              const Icon = getIcon(item.type);
              return (
                <button
                  key={`${item.type}-${idx}`}
                  onClick={() => { navigate(item.path); onClose(); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    idx === selectedIndex
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 text-gray-900 dark:text-white truncate">{item.label}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {getTypeLabel(item.type)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {query.length >= 2 && !loading && allResults.length === 0 && (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Aucun résultat pour "{query}"
          </div>
        )}

        {query.length < 2 && (
          <div className="p-6 text-center text-gray-400 text-sm">
            Tapez au moins 2 caractères pour rechercher
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
